import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { tokenStore } from '@/lib/token-store';
import { AuthContext, type AuthContextValue } from './auth-context';
import { endSession, refreshSession, type UserProfile } from './session';

/**
 * Restores the session on load and holds it for the rest of the app.
 *
 * The access token lives in memory only, so a page reload starts with nothing.
 * The refresh cookie is HttpOnly and survives, so the app asks the API for a
 * fresh access token once at startup — one round-trip in exchange for an XSS
 * being unable to read the long-lived credential.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    void refreshSession().then((tokens) => {
      if (cancelled) {
        return;
      }

      if (tokens) {
        setUser(tokens.user);
      }

      setIsLoading(false);
    });

    return () => {
      // The component can unmount before the probe resolves — in StrictMode it
      // reliably does — and setting state afterwards would warn.
      cancelled = true;
    };
  }, []);

  const setSession = useCallback((nextUser: UserProfile, accessToken: string) => {
    tokenStore.set(accessToken);
    setUser(nextUser);
  }, []);

  const signOut = useCallback(async () => {
    await endSession();
    setUser(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: user !== null,
      isLoading,
      setSession,
      updateUser: setUser,
      signOut,
    }),
    [user, isLoading, setSession, signOut],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}
